import React, { useEffect, useImperativeHandle, useRef, useState, forwardRef } from "react";
import {
  Box,
  Typography,
  Paper,
  Stack,
  TextField,
  Button,
  List,
  ListItem,
  ListItemText,
  ListItemSecondaryAction,
  IconButton,
  Alert,
  Snackbar,
} from "@mui/material";
import MenuBookOutlinedIcon from "@mui/icons-material/MenuBookOutlined";
import LinkIcon from "@mui/icons-material/Link";
import AddRoundedIcon from "@mui/icons-material/AddRounded";
import DeleteOutlineRoundedIcon from "@mui/icons-material/DeleteOutlineRounded";
import AgentDocumentsSection from "../Agents/AgentDocumentsSection";
import { useS3Upload } from "../../hooks/useS3Upload";

export const MAX_TIPO_RAG_DOCS = 8;
export const MAX_TIPO_RAG_URLS = 6;
const ACCEPT_DOCS = ".pdf,.docx,.xls,.xlsx";

function buildDocumentsPrefix() {
  const d = new Date();
  return `types/knowledge/${d.getFullYear()}/${String(d.getMonth() + 1).padStart(2, "0")}`;
}

/**
 * Fontes de conhecimento (documentos + URLs) para tipo/subtipo — mesmo padrão dos agentes FAQ.
 */
const TypeKnowledgeSection = forwardRef(function TypeKnowledgeSection(
  {
    sourceUrls = [],
    documents = [],
    onChange,
    readOnly = false,
    inheritedUrls = [],
    inheritedDocCount = 0,
    parentLabel = "",
  },
  ref
) {
  const [urls, setUrls] = useState([]);
  const [docs, setDocs] = useState([]);
  const [newUrl, setNewUrl] = useState("");
  const [snack, setSnack] = useState({ open: false, message: "", severity: "info" });
  const fileInputRef = useRef(null);
  const documentIdRef = useRef(0);
  const { uploadMany: uploadManyDocs } = useS3Upload();

  useEffect(() => {
    setUrls(Array.isArray(sourceUrls) ? [...sourceUrls] : []);
  }, [sourceUrls]);

  useEffect(() => {
    setDocs(
      (Array.isArray(documents) ? documents : []).map((d, i) => ({
        id: d.id || `persisted_${i}`,
        name: d.file_name || d.name || "documento",
        url: d.url || d.file_url,
        file: null,
      }))
    );
  }, [documents]);

  const emitChange = (nextUrls, nextDocs) => {
    onChange?.({
      source_urls: nextUrls,
      documents: nextDocs
        .filter((d) => d.url && !d.file)
        .map((d) => ({ url: d.url, file_name: d.name })),
      pendingFiles: nextDocs.filter((d) => d.file),
    });
  };

  const handleAddUrl = () => {
    const url = newUrl.trim();
    if (!url) return;
    try {
      // eslint-disable-next-line no-new
      new URL(url);
    } catch {
      setSnack({ open: true, severity: "warning", message: "Informe uma URL válida." });
      return;
    }
    if (urls.includes(url)) return;
    if (urls.length >= MAX_TIPO_RAG_URLS) {
      setSnack({
        open: true,
        severity: "warning",
        message: `Limite de ${MAX_TIPO_RAG_URLS} links. Remova um link antes de adicionar.`,
      });
      return;
    }
    const next = [...urls, url];
    setUrls(next);
    setNewUrl("");
    emitChange(next, docs);
  };

  const handleRemoveUrl = (index) => {
    const next = urls.filter((_, i) => i !== index);
    setUrls(next);
    emitChange(next, docs);
  };

  const validateDocFiles = (fileList) => {
    const exts = [".pdf", ".docx", ".xls", ".xlsx"];
    const accepted = [];
    const errors = [];
    const MAX_MB = 15;
    Array.from(fileList || []).forEach((f) => {
      const name = f?.name || "";
      const ext = name.toLowerCase().slice(name.lastIndexOf("."));
      if (ext === ".doc") {
        errors.push(`${name}: use .docx em vez de .doc.`);
        return;
      }
      if (!exts.includes(ext)) {
        errors.push(`${name}: tipo inválido.`);
      } else if (f.size > MAX_MB * 1024 * 1024) {
        errors.push(`${name}: maior que ${MAX_MB}MB.`);
      } else {
        accepted.push(f);
      }
    });
    if (errors.length) {
      setSnack({ open: true, severity: "warning", message: errors.slice(0, 2).join(" ") });
    }
    return accepted;
  };

  const addDocFiles = (fileList) => {
    const files = validateDocFiles(fileList);
    const available = Math.max(0, MAX_TIPO_RAG_DOCS - docs.length);
    if (!files.length || available <= 0) {
      if (available <= 0) {
        setSnack({
          open: true,
          severity: "warning",
          message: `Limite de ${MAX_TIPO_RAG_DOCS} documentos.`,
        });
      }
      return;
    }
    const trimmed = files.slice(0, available);
    const next = [
      ...docs,
      ...trimmed.map((file) => ({
        id: `tmp_${++documentIdRef.current}`,
        file,
        name: file.name,
        url: null,
      })),
    ];
    setDocs(next);
    emitChange(urls, next);
  };

  const moveDocument = (idx, dir) => {
    const next = [...docs];
    const j = idx + dir;
    if (j < 0 || j >= next.length) return;
    [next[idx], next[j]] = [next[j], next[idx]];
    setDocs(next);
    emitChange(urls, next);
  };

  const removeDocument = (idx) => {
    const next = docs.filter((_, i) => i !== idx);
    setDocs(next);
    emitChange(urls, next);
  };

  useEffect(() => {
    const handler = (e) => addDocFiles(e.detail || []);
    window.addEventListener("agent-documents:addFiles", handler);
    return () => window.removeEventListener("agent-documents:addFiles", handler);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [docs, urls]);

  const prepareForSave = async () => {
    const pending = docs.filter((d) => d.file);
    let uploaded = docs.filter((d) => d.url && !d.file).map((d) => ({
      url: d.url,
      file_name: d.name,
    }));

    if (pending.length) {
      const prefix = buildDocumentsPrefix();
      const results = await uploadManyDocs(
        pending.map((d) => d.file),
        prefix
      );
      uploaded = [
        ...uploaded,
        ...results.map((r, i) => ({
          url: r.url,
          file_name: pending[i]?.name || r.fileName || "document",
        })),
      ];
    }

    return {
      source_urls: urls,
      documents: uploaded.slice(0, MAX_TIPO_RAG_DOCS),
    };
  };

  useImperativeHandle(ref, () => ({ prepareForSave }), [urls, docs]);

  return (
    <Paper variant="outlined" sx={{ p: 2, mt: 2 }}>
      <Typography sx={{ display: "flex", alignItems: "center", gap: 0.75, fontWeight: 600, mb: 1 }}>
        <MenuBookOutlinedIcon fontSize="small" color="primary" />
        Fontes de conhecimento
      </Typography>
      <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
        Documentos e links usados pela IA nos insights do chat e em &quot;Tirar dúvidas&quot; sobre o atendimento.
        {inheritedUrls?.length || inheritedDocCount
          ? ` Valores vazios aqui herdam do nível superior${parentLabel ? ` (${parentLabel})` : ""}: ${inheritedDocCount || 0} doc(s), ${inheritedUrls?.length || 0} link(s).`
          : " Subtipos mais específicos sobrescrevem os anteriores (cascata)."}
      </Typography>

      <Stack spacing={2}>
        <AgentDocumentsSection
          documents={docs.map((d) => ({ id: d.id, name: d.name }))}
          fileInputRef={fileInputRef}
          handleClickAdd={() => fileInputRef.current?.click()}
          handleDrop={(e) => {
            e.preventDefault();
            addDocFiles(e.dataTransfer.files);
          }}
          handleDragOver={(e) => e.preventDefault()}
          moveDocument={moveDocument}
          removeDocument={removeDocument}
          readOnly={readOnly}
          accept={ACCEPT_DOCS}
          embedded
        />

        <Box>
          <Typography variant="subtitle2" color="text.secondary" sx={{ display: "flex", alignItems: "center", gap: 0.5, mb: 0.5 }}>
            <LinkIcon fontSize="small" />
            Links de sites
          </Typography>
          <Stack direction="row" spacing={1} flexWrap="wrap" useFlexGap sx={{ mb: 1 }}>
            <TextField
              size="small"
              placeholder="https://..."
              value={newUrl}
              onChange={(e) => setNewUrl(e.target.value)}
              onKeyDown={(e) => e.key === "Enter" && (e.preventDefault(), handleAddUrl())}
              disabled={readOnly}
              sx={{ minWidth: 260, flex: 1 }}
            />
            <Button
              variant="outlined"
              size="small"
              startIcon={<AddRoundedIcon />}
              onClick={handleAddUrl}
              disabled={readOnly}
            >
              Adicionar
            </Button>
          </Stack>
          {urls.length > 0 && (
            <List dense disablePadding sx={{ bgcolor: "action.hover", borderRadius: 1 }}>
              {urls.map((url, index) => (
                <ListItem key={`${url}-${index}`}>
                  <ListItemText primary={url} primaryTypographyProps={{ variant: "body2", noWrap: true }} />
                  {!readOnly && (
                    <ListItemSecondaryAction>
                      <IconButton size="small" onClick={() => handleRemoveUrl(index)} aria-label="Remover URL">
                        <DeleteOutlineRoundedIcon fontSize="small" />
                      </IconButton>
                    </ListItemSecondaryAction>
                  )}
                </ListItem>
              ))}
            </List>
          )}
        </Box>
      </Stack>

      <Snackbar
        open={snack.open}
        autoHideDuration={5000}
        onClose={() => setSnack((s) => ({ ...s, open: false }))}
        anchorOrigin={{ vertical: "bottom", horizontal: "center" }}
      >
        <Alert severity={snack.severity} onClose={() => setSnack((s) => ({ ...s, open: false }))}>
          {snack.message}
        </Alert>
      </Snackbar>
    </Paper>
  );
});

export default TypeKnowledgeSection;

export async function prepareTypeKnowledgeForSave(knowledgeRef) {
  if (knowledgeRef?.current?.prepareForSave) {
    return knowledgeRef.current.prepareForSave();
  }
  return { source_urls: [], documents: [] };
}
